<script setup lang="ts">
import { AppCard, AppSkeletonLoader, AppTypography } from '~/components/base';
import { useExploreCounts } from '~/composables/useExploreCounts';
import { useLocationStore } from '~/stores/location';
import { $pluralizeWithCount } from '~/utils/pluralize';

const store = useLocationStore();
const { counts, pending, error } = useExploreCounts(
  () => store.location?.suburbId ?? null,
);
</script>

<template>
  <section class="explore-section">
    <AppTypography type="title-6" class="mb-3">Explore</AppTypography>
    <!-- Width comes from the page's centered content column -->
    <div v-if="pending" class="grid grid-cols-2 gap-2 lg:gap-4">
      <AppSkeletonLoader :items="[{ height: '120px' }]" container-class="" />
      <AppSkeletonLoader :items="[{ height: '120px' }]" container-class="" />
    </div>
    <div
      v-else-if="error"
      data-testid="explore-error-state"
      class="explore-section__error flex flex-col items-center gap-2 text-center"
    >
      <AppTypography type="body" class="text-color-tertiary">
        Couldn't load — try again.
      </AppTypography>
    </div>
    <div v-else class="grid grid-cols-2 gap-2 lg:gap-4">
      <NuxtLink to="/parties" class="no-underline">
        <AppCard class="explore-section__card">
          <div class="gap flex flex-col">
            <span class="explore-section__badge">
              <Icon name="solar:flag-2-linear" class="text-xl" />
            </span>
            <AppTypography type="body-bold" class="explore-section__title">
              Parties
            </AppTypography>
            <AppTypography type="footnote" class="text-color-tertiary">
              {{ counts.partiesRegistered }} Registered
            </AppTypography>
          </div>
        </AppCard>
      </NuxtLink>
      <NuxtLink to="/campaigns" class="no-underline">
        <AppCard class="explore-section__card">
          <div class="gap flex flex-col">
            <span class="explore-section__badge">
              <Icon name="solar:notes-linear" class="text-xl" />
            </span>
            <AppTypography type="body-bold" class="explore-section__title">
              Campaigns
            </AppTypography>
            <AppTypography type="footnote" class="text-color-tertiary">
              {{ $pluralizeWithCount(counts.campaignsActive, 'campaign') }}
            </AppTypography>
          </div>
        </AppCard>
      </NuxtLink>
    </div>
  </section>
</template>

<style scoped lang="scss">
.explore-section__card {
  height: 100%;

  // PrimeVue Card already pads its body; padding on the root as well doubles
  // up and leaves dead space below the content (visible vs the Figma).
  // flex:1 + justify-content:center vertically centers the content within
  // the card's full height (the card root is a flex column already).
  :deep(.p-card-body) {
    padding: $spacing-lg;
    flex: 1;
    justify-content: center;
  }
}

.explore-section__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 0.7em;
  border-radius: 9999px;
  background-color: #e8d9bd; // warm tan badge, matching the Figma
  color: #766035;
}

.explore-section__title {
  color: #201e1a;
}

.explore-section__error {
  padding: $spacing-lg;
}
</style>
